import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  ShieldCheck, Plus, Search, Copy, Zap, Download, ArrowLeft,
  CheckCircle2, AlertTriangle, XCircle, Loader2, Clock, FileText, Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  initiateBgvCase, listBgvCases, getBgvCase, updateBgvCheck,
  runBgvIdentityCheck, finalizeBgvCase, downloadBgvReport, bulkInitiateBgvCases,
  getBgvDashboardStats, processScheduledBgv,
} from '../api';

const CHECKS = [
  { key: 'identity',   label: 'Identity',   desc: 'Name, DOB, PAN/Aadhaar authenticity' },
  { key: 'address',    label: 'Address',    desc: 'Current & permanent address proof' },
  { key: 'employment', label: 'Employment', desc: 'Employer, designation, dates, exit reason' },
  { key: 'education',  label: 'Education',  desc: 'Institution, qualification, passing year' },
  { key: 'criminal',   label: 'Criminal',   desc: 'Police/antecedent + criminal-record checks' },
];

const CHECK_STATUS_OPTIONS = [
  { value: 'pending',          label: 'Pending' },
  { value: 'in_progress',      label: 'In Progress' },
  { value: 'verified',         label: 'Verified' },
  { value: 'clear',            label: 'Clear' },
  { value: 'discrepancy',      label: 'Discrepancy' },
  { value: 'unable_to_verify', label: 'Unable to Verify' },
  { value: 'potential_match',  label: 'Potential Match' },
  { value: 'adverse',          label: 'Adverse' },
];

const CHECK_BADGE = (status) => {
  const map = {
    verified:         'bg-emerald-100 text-emerald-800',
    clear:            'bg-emerald-100 text-emerald-800',
    discrepancy:      'bg-amber-100 text-amber-800',
    unable_to_verify: 'bg-slate-200 text-slate-700',
    potential_match:  'bg-orange-100 text-orange-800',
    adverse:          'bg-rose-100 text-rose-800',
    in_progress:      'bg-sky-100 text-sky-800',
    pending:          'bg-slate-100 text-slate-500',
  };
  return map[status] || 'bg-slate-100 text-slate-500';
};

const CASE_BADGE = (status) => {
  const map = {
    consent_pending:            'bg-slate-100 text-slate-700',
    candidate_action_required:  'bg-amber-100 text-amber-800',
    in_progress:                'bg-sky-100 text-sky-800',
    partially_verified:         'bg-sky-100 text-sky-800',
    discrepancy_found:          'bg-amber-100 text-amber-800',
    potential_criminal_match:   'bg-orange-100 text-orange-800',
    manual_review:              'bg-orange-100 text-orange-800',
    verified_clear:             'bg-emerald-100 text-emerald-800',
    adverse:                    'bg-rose-100 text-rose-800',
    disputed:                   'bg-rose-100 text-rose-700',
    completed:                  'bg-indigo-100 text-indigo-800',
  };
  return map[status] || 'bg-slate-100 text-slate-500';
};

const FINAL_RESULT_LABEL = {
  clear:      { text: '🟢 CLEAR',      color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  review:     { text: '🟡 REVIEW',     color: 'bg-amber-100 text-amber-800 border-amber-300' },
  adverse:    { text: '🔴 ADVERSE',    color: 'bg-rose-100 text-rose-800 border-rose-300' },
  incomplete: { text: '⚪ INCOMPLETE', color: 'bg-slate-100 text-slate-700 border-slate-300' },
};

// ─── Dashboard tiles ────────────────────────────────────────────────────

const BGVDashboardTiles = ({ refreshKey }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const { data } = await getBgvDashboardStats(7);
      setStats(data);
    } catch (err) { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchStats(); /* eslint-disable-next-line */ }, [refreshKey]);

  const handleProcessScheduled = async () => {
    setProcessing(true);
    try {
      const { data } = await processScheduledBgv();
      if (data.fired.length === 0) {
        toast.success('No scheduled BGV cases are due right now');
      } else {
        toast.success(`Fired ${data.fired.length} scheduled BGV case${data.fired.length === 1 ? '' : 's'}`);
      }
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process scheduled BGV');
    } finally { setProcessing(false); }
  };

  if (loading || !stats) return null;

  const tiles = [
    { label: 'Total Cases',   value: stats.total_cases,       color: 'from-slate-500 to-slate-700' },
    { label: 'Clear (Pass)',  value: stats.by_final_result.clear + ' / ' + (stats.pass_rate != null ? stats.pass_rate + '%' : '—'), color: 'from-emerald-500 to-emerald-700', testid: 'stat-clear' },
    { label: 'Review',        value: stats.by_final_result.review,     color: 'from-amber-500 to-amber-600' },
    { label: 'Adverse',       value: stats.by_final_result.adverse,    color: 'from-rose-500 to-rose-700' },
    { label: 'Avg TAT',       value: stats.tat_hours.avg != null ? stats.tat_hours.avg + ' h' : '—', color: 'from-sky-500 to-sky-700',
      subtitle: stats.tat_hours.p50 != null ? `p50 ${stats.tat_hours.p50}h · p90 ${stats.tat_hours.p90}h` : null,
      testid: 'stat-tat' },
    { label: `Stuck > ${stats.stuck_days_threshold}d`, value: stats.stuck_cases.length, color: 'from-orange-500 to-orange-700', testid: 'stat-stuck' },
  ];

  return (
    <div className="space-y-4" data-testid="bgv-dashboard-tiles">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {tiles.map((t, i) => (
          <div key={i}
            className={`rounded-lg p-4 bg-gradient-to-br ${t.color} text-white shadow-sm`}
            data-testid={t.testid || `stat-${i}`}>
            <p className="text-xs opacity-80 uppercase tracking-wide">{t.label}</p>
            <p className="text-2xl font-bold mt-1">{t.value}</p>
            {t.subtitle && <p className="text-[10px] opacity-75 mt-1">{t.subtitle}</p>}
          </div>
        ))}
      </div>

      {stats.stuck_cases.length > 0 && (
        <div className="rounded-md border border-orange-200 bg-orange-50 p-3" data-testid="stuck-cases-list">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-orange-900 flex items-center gap-2">
              <AlertTriangle size={14} />
              Cases stuck for &gt; {stats.stuck_days_threshold} days
            </p>
            <Button variant="ghost" size="sm" onClick={handleProcessScheduled} disabled={processing}>
              {processing ? <Loader2 size={12} className="animate-spin mr-1" /> : <Zap size={12} className="mr-1" />}
              Run scheduled
            </Button>
          </div>
          <div className="space-y-1 text-xs">
            {stats.stuck_cases.slice(0, 5).map(c => (
              <div key={c.id} className="flex items-center justify-between border-t border-orange-200 pt-1">
                <div>
                  <span className="font-mono">{c.case_number}</span>{' '}
                  <span className="text-slate-600">{c.candidate_name}</span>{' '}
                  <span className="text-slate-500">· {c.status}</span>
                </div>
                <span className="text-orange-700 font-medium">{c.days_stuck} days</span>
              </div>
            ))}
            {stats.stuck_cases.length > 5 && (
              <p className="text-[11px] text-orange-800 pt-1">
                +{stats.stuck_cases.length - 5} more — see full list below
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── List page ──────────────────────────────────────────────────────────

const BGVCasesList = ({ onOpenCase, refreshKey, onRefresh }) => {
  const [cases, setCases] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newCase, setNewCase] = useState({
    candidate_name: '', candidate_email: '', candidate_phone: '', offer_id: '',
  });
  const [lastPortalLink, setLastPortalLink] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const fetchCases = async () => {
    setLoading(true);
    try {
      const { data } = await listBgvCases({
        q: query || undefined,
        status: statusFilter || undefined,
        page: 1, page_size: 50,
      });
      setCases(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load cases');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCases(); /* eslint-disable-next-line */ }, [refreshKey]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newCase.candidate_name.trim() || !newCase.candidate_email.trim()) {
      toast.error('Name & email are required');
      return;
    }
    setCreating(true);
    try {
      const { data } = await initiateBgvCase({
        ...newCase,
        offer_id: newCase.offer_id || null,
      });
      setLastPortalLink(data.portal_link);
      toast.success(`Case ${data.case.case_number} created — invite emailed to candidate`);
      setNewCase({ candidate_name: '', candidate_email: '', candidate_phone: '', offer_id: '' });
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate BGV');
    } finally {
      setCreating(false);
    }
  };

  const copyPortalLink = () => {
    if (!lastPortalLink) return;
    navigator.clipboard.writeText(lastPortalLink);
    toast.success('Portal link copied');
  };

  const downloadCsvTemplate = () => {
    const csv = 'candidate_name,candidate_email,candidate_phone,offer_id\n' +
      'John Doe,john@example.com,9876543210,\n' +
      'Jane Smith,jane@example.com,,\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'bgv-bulk-template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const parseCsv = (text) => {
    // Minimal CSV parser: no quoted-comma support (fine for our simple template)
    const lines = text.trim().split(/\r?\n/).filter(Boolean);
    if (!lines.length) return [];
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    return lines.slice(1).map(line => {
      const cells = line.split(',');
      const row = {};
      headers.forEach((h, i) => { row[h] = (cells[i] || '').trim(); });
      return row;
    });
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) { toast.error('Please choose a CSV file'); return; }
    setBulkUploading(true);
    setBulkResult(null);
    try {
      const text = await bulkFile.text();
      const rows = parseCsv(text)
        .filter(r => r.candidate_name && r.candidate_email);
      if (rows.length === 0) {
        toast.error('CSV has no valid rows (candidate_name + candidate_email required)');
        setBulkUploading(false);
        return;
      }
      const candidates = rows.map(r => ({
        candidate_name: r.candidate_name,
        candidate_email: r.candidate_email,
        candidate_phone: r.candidate_phone || null,
        offer_id: r.offer_id || null,
      }));
      const { data } = await bulkInitiateBgvCases(candidates, 'standard');
      setBulkResult(data);
      toast.success(
        `Created ${data.created.length} of ${data.total} cases` +
        (data.skipped.length ? ` (${data.skipped.length} skipped)` : '') +
        (data.failed.length ? ` — ${data.failed.length} failed` : ''),
      );
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Bulk upload failed');
    } finally {
      setBulkUploading(false);
    }
  };

  const downloadBulkErrorsCsv = () => {
    if (!bulkResult) return;
    const rows = [
      ...bulkResult.skipped.map(r => ({ ...r, kind: 'skipped' })),
      ...bulkResult.failed.map(r => ({ ...r, kind: 'failed' })),
    ];
    const csv = 'row,candidate_email,kind,reason\n' +
      rows.map(r => `${r.row || ''},${r.candidate_email || ''},${r.kind},${(r.reason || '').replace(/,/g, ';')}`).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'bgv-bulk-errors.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="bgv-list-page">
      <BGVDashboardTiles refreshKey={refreshKey} />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <ShieldCheck className="text-indigo-600" />
            Background Verification
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Standard 5-check package: Identity · Address · Employment · Education · Criminal
          </p>
        </div>
        <div className="flex gap-2 items-start flex-wrap">
        <Dialog open={bulkOpen} onOpenChange={(o) => { setBulkOpen(o); if (!o) { setBulkFile(null); setBulkResult(null); } }}>
          <DialogTrigger asChild>
            <Button variant="outline" data-testid="bulk-bgv-btn">
              <Upload size={16} className="mr-2" /> Bulk Upload
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Bulk Initiate BGV Cases</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="rounded-md bg-indigo-50 border border-indigo-200 p-3 text-xs">
                <p>Upload a CSV with columns: <code>candidate_name, candidate_email, candidate_phone, offer_id</code></p>
                <p className="mt-1">Skips duplicates (email with an open case). Sends the portal link to each candidate.</p>
                <Button variant="ghost" size="sm" className="mt-2 h-7 px-2 text-xs"
                  onClick={downloadCsvTemplate} data-testid="bgv-csv-template-btn">
                  <Download size={12} className="mr-1" /> Download CSV template
                </Button>
              </div>
              <div className="space-y-2">
                <Label>CSV file</Label>
                <Input type="file" accept=".csv,text/csv"
                  onChange={(e) => { setBulkFile(e.target.files?.[0] || null); setBulkResult(null); }}
                  data-testid="bulk-bgv-file-input" />
                {bulkFile && <p className="text-xs text-slate-500">{bulkFile.name}</p>}
              </div>
              {bulkResult && (
                <div className="rounded-md border border-slate-200 p-3 text-xs space-y-2">
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-emerald-50 rounded p-2">
                      <div className="text-lg font-bold text-emerald-700">{bulkResult.created.length}</div>
                      <div className="text-emerald-600">Created</div>
                    </div>
                    <div className="bg-amber-50 rounded p-2">
                      <div className="text-lg font-bold text-amber-700">{bulkResult.skipped.length}</div>
                      <div className="text-amber-600">Skipped</div>
                    </div>
                    <div className="bg-rose-50 rounded p-2">
                      <div className="text-lg font-bold text-rose-700">{bulkResult.failed.length}</div>
                      <div className="text-rose-600">Failed</div>
                    </div>
                  </div>
                  {(bulkResult.skipped.length + bulkResult.failed.length > 0) && (
                    <div>
                      <Button variant="outline" size="sm" onClick={downloadBulkErrorsCsv}
                        data-testid="bgv-bulk-errors-download">
                        <Download size={12} className="mr-1" /> Download errors CSV
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button onClick={handleBulkUpload} disabled={bulkUploading || !bulkFile}
                data-testid="bulk-bgv-submit-btn">
                {bulkUploading ? <Loader2 size={14} className="animate-spin mr-2" /> : <Upload size={14} className="mr-2" />}
                Initiate for all
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-bgv-btn">
              <Plus size={16} className="mr-2" /> New BGV Case
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Initiate Background Verification</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label>Candidate Name *</Label>
                <Input value={newCase.candidate_name}
                  onChange={(e) => setNewCase({ ...newCase, candidate_name: e.target.value })}
                  data-testid="bgv-name-input" required />
              </div>
              <div className="space-y-2">
                <Label>Candidate Email *</Label>
                <Input type="email" value={newCase.candidate_email}
                  onChange={(e) => setNewCase({ ...newCase, candidate_email: e.target.value })}
                  data-testid="bgv-email-input" required />
              </div>
              <div className="space-y-2">
                <Label>Candidate Phone</Label>
                <Input value={newCase.candidate_phone}
                  onChange={(e) => setNewCase({ ...newCase, candidate_phone: e.target.value })}
                  data-testid="bgv-phone-input" />
              </div>
              <div className="space-y-2">
                <Label>Offer ID (optional)</Label>
                <Input value={newCase.offer_id}
                  onChange={(e) => setNewCase({ ...newCase, offer_id: e.target.value })}
                  placeholder="Link to Offer Letter" />
              </div>
              {lastPortalLink && (
                <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-xs">
                  <p className="font-medium text-emerald-900 mb-1">Candidate portal link:</p>
                  <p className="font-mono break-all text-emerald-800">{lastPortalLink}</p>
                  <Button type="button" size="sm" variant="outline" className="mt-2"
                    onClick={copyPortalLink}>
                    <Copy size={12} className="mr-1" /> Copy
                  </Button>
                </div>
              )}
              <DialogFooter>
                <Button type="submit" disabled={creating} data-testid="bgv-submit-btn">
                  {creating ? <Loader2 size={14} className="animate-spin mr-2" /> : <Plus size={14} className="mr-2" />}
                  Create & Email Invite
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <Input placeholder="Search by name, email, case number..." value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchCases()}
            className="pl-9" data-testid="bgv-search-input" />
        </div>
        <Select value={statusFilter || 'all'}
          onValueChange={(v) => { setStatusFilter(v === 'all' ? '' : v); }}>
          <SelectTrigger className="w-[220px]"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {['consent_pending','candidate_action_required','in_progress','partially_verified',
              'discrepancy_found','potential_criminal_match','manual_review',
              'verified_clear','adverse','disputed','completed'].map(s =>
                <SelectItem key={s} value={s}>{s.replace(/_/g,' ')}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={fetchCases}>Apply</Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            All cases <span className="text-slate-400 text-sm font-normal">({total})</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-10 text-slate-500">
              <Loader2 className="animate-spin inline mr-2" size={16} /> Loading…
            </div>
          ) : cases.length === 0 ? (
            <div className="text-center py-10 text-slate-500 text-sm">
              No BGV cases yet. Click <strong>New BGV Case</strong> to initiate one.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-slate-500 text-left">
                    <th className="p-3 font-medium">Case #</th>
                    <th className="p-3 font-medium">Candidate</th>
                    <th className="p-3 font-medium">Status</th>
                    <th className="p-3 font-medium">Final Result</th>
                    <th className="p-3 font-medium">Initiated</th>
                    <th className="p-3 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {cases.map(c => (
                    <tr key={c.id} className="border-t hover:bg-slate-50" data-testid={`bgv-row-${c.case_number}`}>
                      <td className="p-3 font-mono text-xs">{c.case_number}</td>
                      <td className="p-3">
                        <div className="font-medium text-slate-900">{c.candidate_name}</div>
                        <div className="text-xs text-slate-500">{c.candidate_email}</div>
                      </td>
                      <td className="p-3">
                        <Badge className={CASE_BADGE(c.status)}>
                          {(c.status || '').replace(/_/g, ' ')}
                        </Badge>
                      </td>
                      <td className="p-3">
                        {c.final_result && (
                          <Badge className={FINAL_RESULT_LABEL[c.final_result]?.color || ''}>
                            {FINAL_RESULT_LABEL[c.final_result]?.text || c.final_result}
                          </Badge>
                        )}
                      </td>
                      <td className="p-3 text-xs text-slate-500">
                        {c.initiated_at ? new Date(c.initiated_at).toLocaleDateString('en-IN') : '-'}
                        <div className="text-[10px]">{c.initiated_by_name}</div>
                      </td>
                      <td className="p-3 text-right">
                        <Button variant="outline" size="sm"
                          onClick={() => onOpenCase(c.id)}
                          data-testid={`open-bgv-${c.case_number}`}>Open</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ─── Detail page ────────────────────────────────────────────────────────

const BGVCaseDetail = ({ caseId, onBack, onChanged }) => {
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runningIdentity, setRunningIdentity] = useState(false);
  const [editCheck, setEditCheck] = useState(null); // { key, status, vendor, ref, notes }
  const [savingCheck, setSavingCheck] = useState(false);
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [finalPayload, setFinalPayload] = useState({ final_result: 'clear', hr_notes: '' });

  const fetchCase = async () => {
    setLoading(true);
    try {
      const { data } = await getBgvCase(caseId);
      setCaseData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load case');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCase(); /* eslint-disable-next-line */ }, [caseId]);

  const handleRunIdentity = async () => {
    setRunningIdentity(true);
    try {
      const { data } = await runBgvIdentityCheck(caseId);
      setCaseData(data);
      toast.success(`Identity check: ${data.checks.identity.status}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Identity check failed');
    } finally {
      setRunningIdentity(false);
    }
  };

  const openEditCheck = (key) => {
    const c = caseData.checks[key] || {};
    setEditCheck({ key, status: c.status || 'pending', vendor: c.vendor || '',
      reference_number: c.reference_number || '', notes: c.notes || '' });
  };

  const saveCheck = async () => {
    setSavingCheck(true);
    try {
      const { data } = await updateBgvCheck(caseId, editCheck.key, {
        status: editCheck.status,
        vendor: editCheck.vendor || null,
        reference_number: editCheck.reference_number || null,
        notes: editCheck.notes || null,
      });
      setCaseData(data);
      setEditCheck(null);
      toast.success('Check updated');
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update check');
    } finally {
      setSavingCheck(false);
    }
  };

  const handleFinalize = async () => {
    setFinalizing(true);
    try {
      const { data } = await finalizeBgvCase(caseId, finalPayload);
      setCaseData(data);
      setFinalizeOpen(false);
      toast.success('Case finalized');
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to finalize');
    } finally {
      setFinalizing(false);
    }
  };

  const handleDownload = async () => {
    try {
      const { data } = await downloadBgvReport(caseId);
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${caseData.case_number}-BGV-Report.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('BGV report downloaded');
    } catch (err) {
      toast.error('Failed to download report');
    }
  };

  if (loading || !caseData) {
    return (
      <div className="text-center py-20 text-slate-500">
        <Loader2 className="animate-spin inline mr-2" /> Loading case…
      </div>
    );
  }

  const c = caseData;
  const hasPan = c.form?.pan;

  return (
    <div className="space-y-6" data-testid="bgv-detail-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Button variant="ghost" size="sm" onClick={onBack} className="mb-2">
            <ArrowLeft size={14} className="mr-1" /> All Cases
          </Button>
          <h1 className="text-2xl font-bold text-slate-900">
            {c.case_number}
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            <strong>{c.candidate_name}</strong> · {c.candidate_email}
            {c.candidate_phone && ` · ${c.candidate_phone}`}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap items-start">
          <Badge className={CASE_BADGE(c.status) + ' text-sm px-3 py-1'}>
            {(c.status || '').replace(/_/g,' ')}
          </Badge>
          {c.final_result && (
            <Badge className={(FINAL_RESULT_LABEL[c.final_result]?.color || '') + ' text-sm px-3 py-1 border'}>
              {FINAL_RESULT_LABEL[c.final_result]?.text}
            </Badge>
          )}
        </div>
      </div>

      {/* Candidate consent + form snapshot */}
      <Card>
        <CardHeader><CardTitle className="text-base">Candidate Submission</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-slate-500 text-xs uppercase mb-1">Consent</p>
            {c.consent?.given ? (
              <p className="text-emerald-700 flex items-center gap-1">
                <CheckCircle2 size={14} /> Given by <strong>{c.consent.typed_name}</strong>{' '}
                on {new Date(c.consent.given_at).toLocaleString('en-IN')}
              </p>
            ) : (
              <p className="text-amber-700 flex items-center gap-1">
                <Clock size={14} /> Awaiting candidate consent
              </p>
            )}
          </div>
          <div>
            <p className="text-slate-500 text-xs uppercase mb-1">OTP</p>
            <p className={c.otp_verified ? 'text-emerald-700' : 'text-slate-500'}>
              {c.otp_verified ? 'Verified' : 'Not verified'}
            </p>
          </div>
          <div className="md:col-span-2 grid grid-cols-2 gap-2">
            <div><span className="text-slate-500">PAN:</span> {c.form?.pan || '—'}</div>
            <div><span className="text-slate-500">Aadhaar (last 4):</span> {c.form?.aadhaar_last4 || '—'}</div>
            <div><span className="text-slate-500">DOB:</span> {c.form?.dob || '—'}</div>
            <div><span className="text-slate-500">Current address:</span> {c.form?.current_address || '—'}</div>
            <div><span className="text-slate-500">Permanent address:</span> {c.form?.permanent_address || '—'}</div>
          </div>
          {(c.form?.education?.length > 0 || c.form?.employment?.length > 0) && (
            <div className="md:col-span-2 grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-slate-500 text-xs uppercase mb-1">Education</p>
                {(c.form?.education || []).map((e, i) => (
                  <div key={i} className="border-l-2 border-indigo-200 pl-2 mb-1 text-xs">
                    <strong>{e.institution}</strong> — {e.qualification} ({e.passing_year})
                  </div>
                ))}
              </div>
              <div>
                <p className="text-slate-500 text-xs uppercase mb-1">Employment</p>
                {(c.form?.employment || []).map((e, i) => (
                  <div key={i} className="border-l-2 border-indigo-200 pl-2 mb-1 text-xs">
                    <strong>{e.employer}</strong> — {e.designation}{' '}
                    ({e.start_date} → {e.end_date || 'present'})
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Verification checks */}
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-base">Verification Checks</CardTitle>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={handleRunIdentity}
              disabled={!hasPan || runningIdentity}
              title={!hasPan ? 'Candidate must submit PAN first' : 'Verify PAN against NSDL via Cashfree'}
              data-testid="run-identity-check-btn">
              {runningIdentity ? <Loader2 size={14} className="animate-spin mr-1" /> : <Zap size={14} className="mr-1" />}
              Auto-verify Identity
            </Button>
            <Button size="sm" variant="outline" onClick={handleDownload} data-testid="download-report-btn">
              <FileText size={14} className="mr-1" /> Download PDF
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {CHECKS.map(chk => {
            const val = c.checks?.[chk.key] || {};
            return (
              <div key={chk.key}
                className="flex items-start justify-between gap-3 border border-slate-200 rounded-md p-3 hover:border-indigo-300 transition-colors"
                data-testid={`bgv-check-${chk.key}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-slate-800">{chk.label} Verification</p>
                    <Badge className={CHECK_BADGE(val.status)}>
                      {(val.status || 'pending').replace(/_/g,' ')}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{chk.desc}</p>
                  {(val.vendor || val.reference_number || val.notes) && (
                    <div className="mt-2 text-xs text-slate-600 space-y-0.5">
                      {val.vendor && <div><span className="text-slate-400">Vendor:</span> {val.vendor}</div>}
                      {val.reference_number && <div><span className="text-slate-400">Ref:</span> {val.reference_number}</div>}
                      {val.notes && <div className="italic">{val.notes}</div>}
                      {val.verified_at && (
                        <div className="text-[10px] text-slate-400">
                          {val.verified_by} · {new Date(val.verified_at).toLocaleString('en-IN')}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <Button variant="outline" size="sm" onClick={() => openEditCheck(chk.key)}
                  data-testid={`edit-check-${chk.key}`}>
                  Update
                </Button>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Finalize */}
      <Card>
        <CardHeader><CardTitle className="text-base">Final Decision</CardTitle></CardHeader>
        <CardContent className="flex justify-between items-center gap-4 flex-wrap">
          <div className="text-sm text-slate-600">
            {c.final_result
              ? <>Finalized by <strong>{c.completed_by}</strong> on {new Date(c.completed_at).toLocaleDateString('en-IN')}</>
              : 'Once all checks are reviewed, record the final BGV result to close this case.'}
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={handleDownload}
              data-testid="download-branded-pdf-btn"
              className="border-indigo-300 text-indigo-700 hover:bg-indigo-50">
              <FileText size={14} className="mr-2" /> Download Branded PDF
            </Button>
            <Dialog open={finalizeOpen} onOpenChange={setFinalizeOpen}>
            <DialogTrigger asChild>
              <Button data-testid="finalize-btn">
                {c.final_result ? 'Update Result' : 'Finalize Case'}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Finalize BGV Case</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Final Result *</Label>
                  <Select value={finalPayload.final_result}
                    onValueChange={(v) => setFinalPayload({ ...finalPayload, final_result: v })}>
                    <SelectTrigger data-testid="final-result-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="clear">🟢 CLEAR — All checks satisfactory</SelectItem>
                      <SelectItem value="review">🟡 REVIEW — Discrepancy needs review</SelectItem>
                      <SelectItem value="adverse">🔴 ADVERSE — Material adverse finding</SelectItem>
                      <SelectItem value="incomplete">⚪ INCOMPLETE — Could not be completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>HR Notes</Label>
                  <Textarea value={finalPayload.hr_notes}
                    onChange={(e) => setFinalPayload({ ...finalPayload, hr_notes: e.target.value })}
                    placeholder="Reasoning, edge cases, decisions..." />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={handleFinalize} disabled={finalizing} data-testid="confirm-finalize-btn">
                  {finalizing ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
                  Record Final Result
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        </CardContent>
      </Card>

      {/* Timeline */}
      <Card>
        <CardHeader><CardTitle className="text-base">Timeline</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-2 text-xs">
            {(c.timeline || []).slice().reverse().map((t, i) => (
              <div key={i} className="flex gap-3 border-l-2 border-indigo-200 pl-3 py-1">
                <div className="text-slate-400 w-40 shrink-0">
                  {new Date(t.at).toLocaleString('en-IN')}
                </div>
                <div className="flex-1">
                  <span className="font-medium">{t.event}</span>{' '}
                  <span className="text-slate-500">by {t.actor}</span>
                  {t.note && <span className="text-slate-500"> — {t.note}</span>}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Edit-check dialog */}
      <Dialog open={!!editCheck} onOpenChange={(o) => !o && setEditCheck(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update {editCheck?.key} check</DialogTitle>
          </DialogHeader>
          {editCheck && (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={editCheck.status}
                  onValueChange={(v) => setEditCheck({ ...editCheck, status: v })}>
                  <SelectTrigger data-testid="check-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CHECK_STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Vendor</Label>
                <Input value={editCheck.vendor}
                  onChange={(e) => setEditCheck({ ...editCheck, vendor: e.target.value })}
                  placeholder="e.g. AuthBridge, Local field agent" />
              </div>
              <div className="space-y-2">
                <Label>Reference #</Label>
                <Input value={editCheck.reference_number}
                  onChange={(e) => setEditCheck({ ...editCheck, reference_number: e.target.value })}
                  placeholder="Vendor case ID / report ID" />
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea value={editCheck.notes}
                  onChange={(e) => setEditCheck({ ...editCheck, notes: e.target.value })}
                  placeholder="Findings, remarks..." />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={saveCheck} disabled={savingCheck} data-testid="save-check-btn">
              {savingCheck ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ─── Page shell ─────────────────────────────────────────────────────────

const BGVCases = () => {
  const [openId, setOpenId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6">
        {openId ? (
          <BGVCaseDetail caseId={openId}
            onBack={() => setOpenId(null)}
            onChanged={() => setRefreshKey(k => k + 1)} />
        ) : (
          <BGVCasesList
            refreshKey={refreshKey}
            onRefresh={() => setRefreshKey(k => k + 1)}
            onOpenCase={(id) => setOpenId(id)} />
        )}
      </div>
    </Layout>
  );
};

export default BGVCases;
